import { HelpCircleFilled } from '@creditornot/cb-icons';
import { colors } from '@creditornot/cb-ingredients';
import { LegacyPopover } from '@creditornot/cb-popover';
import { Spinner } from '@creditornot/cb-progress';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { PurchasesTotals } from 'apis/merchant-gateway/merchant-analytics/types';
import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { typographyCss } from 'common/typography';

const Container = styled.div`
  display: flex;
  justify-items: stretch;
  flex-wrap: wrap;
  gap: 8px 24px;
`;

const AmountContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 80px; /* To reduce jumping from value/loading change */
`;

const Label = styled.div`
  ${typographyCss.Small}
  display: flex;
  align-items: center;
  gap: 4px;
`;

const InfoPopover = styled(LegacyPopover)`
  display: block;
  height: 12px;
`;

const InfoIcon = styled(HelpCircleFilled)`
  width: 12px;
  height: 12px;
  fill: ${cssThemedValue({ light: colors.pepper24, dark: colors.salt28 })};
`;

const Tooltip = styled.span`
  ${typographyCss.Body3}
  display: block;
  padding: 4px;
  line-height: 20px;
  max-width: 300px;
`;

const Amount = styled.div`
  ${typographyCss.Heading6}
`;

type PurchasesTableTotalsProps = {
  className?: string;
  purchasesTotals: PurchasesTotals | undefined;
  showFees: boolean;
  currency: Currency;
  isLoading: boolean;
};

const DEFAULT_PURCHASES_TOTALS: PurchasesTotals = {
  count: 0,
  salesDelivered: { gross: 0 },
  salesRejected: { gross: 0 },
  feesDelivered: { gross: 0 },
};

const PurchasesTableTotals = ({
  className,
  purchasesTotals = DEFAULT_PURCHASES_TOTALS,
  showFees,
  currency,
  isLoading,
}: PurchasesTableTotalsProps) => {
  return (
    <Container className={className}>
      <AmountContainer>
        <Label>
          <FormattedMessage id="purchases.sales.total" tagName="span" />
          <InfoPopover
            content={
              <Tooltip>
                <FormattedMessage id="purchases.sales-tooltip.total" />
              </Tooltip>
            }
          >
            <InfoIcon />
          </InfoPopover>
        </Label>
        <Amount>
          {isLoading ? (
            <Spinner size="small" />
          ) : (
            <MonetaryAmount amount={purchasesTotals.salesDelivered.gross} currency={currency} />
          )}
        </Amount>
      </AmountContainer>
      <AmountContainer>
        <Label>
          <FormattedMessage id="purchases.sales.rejected" tagName="span" />
          <InfoPopover
            content={
              <Tooltip>
                <Tooltip>
                  <FormattedMessage id="purchases.sales-tooltip.rejected" />
                </Tooltip>
              </Tooltip>
            }
          >
            <InfoIcon />
          </InfoPopover>
        </Label>
        <Amount>
          {isLoading ? (
            <Spinner size="small" />
          ) : (
            <MonetaryAmount amount={purchasesTotals.salesRejected.gross} currency={currency} />
          )}
        </Amount>
      </AmountContainer>
      {showFees && (
        <AmountContainer>
          <Label>
            <FormattedMessage id="purchase-history.fees" tagName="span" />
            <InfoPopover
              content={
                <Tooltip>
                  <Tooltip>
                    <FormattedMessage id="purchases.sales-tooltip.fees" />
                  </Tooltip>
                </Tooltip>
              }
            >
              <InfoIcon />
            </InfoPopover>
          </Label>
          <Amount>
            {isLoading ? (
              <Spinner size="small" />
            ) : (
              <MonetaryAmount amount={purchasesTotals.feesDelivered.gross} currency={currency} />
            )}
          </Amount>
        </AmountContainer>
      )}
    </Container>
  );
};

export default PurchasesTableTotals;
